import { makeStyles, Button as MaterialButton } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DOMPurify from 'dompurify';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import eventsAPI, { EventId, LeadMentorReportData } from '../../api/events';
import { Role } from '../../api/user/Role';
import { PageLoader, Link, Text } from '../../components/common';
import { SessionContext } from '../../contexts/session-context';
import { UserContext } from '../../contexts/user-context';
import { getRoutePath, Pages } from '../../router/constants';
import { createMarkup } from '../../utils/functions';

const useStyles = makeStyles({
  container: {
    maxWidth: 670,
  },
  loader: {
    width: '100%',
    minHeight: 400,
    maxHeight: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 500,
    maxWidth: '80%',
    gap: 16,
    textAlign: 'center',
  },
  bodyText: {
    textAlign: 'left',
    wordBreak: 'break-word',

    '& ul, ol, li, b, i, u, h1, h2, pre': {
      margin: 'revert',
      padding: 'revert',
      fontWeight: 'revert',
      fontStyle: 'revert',
      listStyle: 'revert',
      border: 'revert',
      fontSize: 'revert',
      font: 'revert',
      verticalAlign: 'revert',
      lineHeight: 'normal !important',
    },
  },
});

function SessionReportPage() {
  const classes = useStyles();
  const { sessionId } = useParams<{ sessionId: EventId }>();
  const { tokenData, hasAccessToAction, hasRole } = useContext(UserContext);
  const { mentors } = useContext(SessionContext);

  const [isLoading, setIsLoading] = useState(false);
  const [mentorReport, setMentorReport] = useState<LeadMentorReportData>();

  const isMentorNotParticipant = useMemo(() => {
    const isMentor = hasRole(Role.Mentor);

    if (!isMentor) {
      return false;
    }

    const identityId = tokenData?.identityid;

    if (!identityId) {
      return true;
    }

    return mentors.every((mentor) => mentor.id !== identityId);
  }, [hasRole, mentors, tokenData?.identityid]);

  useEffect(() => {
    const loadReport = async (sessionId: string) => {
      if (!sessionId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const lastLeadMentorReport = await eventsAPI.getLeadMentorReportLast(
          sessionId,
        );

        if (lastLeadMentorReport) {
          setMentorReport(lastLeadMentorReport);
        }
      } catch (error: any) {
        console.error('Error loading report:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadReport(sessionId);
  }, [sessionId]);

  if (isLoading) {
    return (
      <div className={classes.loader}>
        <PageLoader />
      </div>
    );
  }

  const hasAccessToReport =
    hasAccessToAction('session.details.leadMentorReport') &&
    !isMentorNotParticipant;

  if (!mentorReport) {
    return (
      <div className={classes.loader}>
        <div className={classes.emptyState}>
          <Text variant='normal' testid='session-no-report'>
            {hasAccessToReport ? (
              <>
                A lead advisor report for this session has not been submitted
                yet.
                <br />
                You can submit the report by clicking on a button below.
              </>
            ) : (
              <>
                A lead advisor report for this session has not been submitted
                yet.
              </>
            )}
          </Text>
          {hasAccessToReport && (
            <MaterialButton
              component={Link}
              to={getRoutePath(Pages.SESSIONS_LEAD_MENTOR, {
                sessionId,
              })}
              startIcon={<AddIcon />}
              variant='contained'
              color='primary'>
              Report
            </MaterialButton>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Text variant='normal'>
        <div
          className={classes.bodyText}
          dangerouslySetInnerHTML={createMarkup(
            DOMPurify.sanitize(mentorReport.contents),
          )}
        />
      </Text>
    </div>
  );
}

export default SessionReportPage;
